'use client';

import { useEffect, useCallback, useState, useRef } from 'react';
import { WSClient } from './ws-client';
import type { ChatApi, ChatWebSocketOptions } from './api-types';
import type { ChatPlatform } from './types';
import {
  asString,
  normalizeChatMessage,
  normalizeChatMessagePatch,
} from './normalizers';
import {
  asChatMessageList,
  asChatRoomList,
  chatMessageIdEqual,
  useChatStore,
} from './store';

let globalWS: WSClient | null = null;
let globalHandlersRegistered = false;
let globalApi: ChatApi | null = null;
let globalGetUser: (() => { id: number } | null) | null = null;
let globalPlatform: ChatPlatform = 'pwa';

export const CHAT_ADMIN_INCOMING_EVENT = 'chat:admin-incoming';

const connectionSubscribers = new Set<(connected: boolean) => void>();
const typingTimeouts = new Map<string, number>();

function notifyConnectionSubscribers(connected: boolean) {
  connectionSubscribers.forEach((subscriber) => subscriber(connected));
}

function dispatchAdminIncomingToast(
  rid: number,
  normalized: ReturnType<typeof normalizeChatMessage>,
  room: ReturnType<typeof asChatRoomList>[number] | undefined,
  incrementUnread: boolean,
  isOwnMessage: boolean
) {
  if (typeof window === 'undefined' || globalPlatform !== 'admin') return;
  if (!incrementUnread || isOwnMessage || !normalized) return;
  const latestRoom =
    asChatRoomList(useChatStore.getState().rooms).find((item) => item.id === rid) ?? room;
  if (latestRoom?.is_muted) return;
  window.dispatchEvent(
    new CustomEvent(CHAT_ADMIN_INCOMING_EVENT, {
      detail: { room: latestRoom ?? { id: rid, type: 'direct' }, message: normalized },
    })
  );
}

function registerGlobalHandlers() {
  if (!globalWS || globalHandlersRegistered || !globalApi) return;
  globalHandlersRegistered = true;

  globalWS.on('message.new', (data) => {
    const normalized = normalizeChatMessage(data, { source: 'ws.message.new' });
    if (!normalized) return;

    const currentUserId = globalGetUser?.()?.id;
    const chatState = useChatStore.getState();
    const rid = Number(normalized.room_id);
    if (!Number.isFinite(rid) || rid < 1) return;

    const roomsList = asChatRoomList(chatState.rooms);
    const room = roomsList.find((item) => item.id === rid);
    const isOwnMessage = String(normalized.sender_user_id) === String(currentUserId);
    const active = chatState.activeRoomId;
    const inOpenRoom = active != null && Number(active) === rid;
    const msgTopic = normalized.topic_id != null ? Number(normalized.topic_id) : null;
    const activeForumTopic = chatState.activeForumTopicId;
    const viewingSameForumTopic =
      Boolean(room?.forum_enabled) &&
      inOpenRoom &&
      msgTopic != null &&
      activeForumTopic != null &&
      msgTopic === activeForumTopic;
    const isSystem = normalized.message_type === 'system';
    const incrementUnread =
      !isSystem &&
      !isOwnMessage &&
      (!inOpenRoom || (Boolean(room?.forum_enabled) && !viewingSameForumTopic));

    if (isOwnMessage) {
      chatState.removePendingFromSender(rid, String(normalized.sender_user_id ?? ''));
    }

    const isSupportFeed =
      room?.entity_type === 'support' ||
      room?.entity_type === 'support_case' ||
      (typeof room?.name === 'string' && room.name.toLowerCase().includes('поддержка'));
    const isChannelThreadReply =
      room?.type === 'channel' && normalized.reply_to_id != null && !isSupportFeed;

    if (isChannelThreadReply) {
      if (incrementUnread && !isOwnMessage) chatState.incrementUnread(rid);
      dispatchAdminIncomingToast(rid, normalized, room, incrementUnread, isOwnMessage);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('chat:channel-thread-message', {
            detail: { ...normalized, room_id: rid },
          })
        );
      }
    } else {
      chatState.addMessage(rid, normalized);
      const hadRoomInList = roomsList.some((item) => item.id === rid);
      chatState.touchRoom(
        Number(normalized.room_id),
        {
          content:
            normalized.content ||
            (asString(normalized.message_type) ? `[${asString(normalized.message_type)}]` : '[Сообщение]'),
          created_at: asString(normalized.created_at) || new Date().toISOString(),
          sender_name: asString(normalized.sender_name),
          message_type: asString(normalized.message_type),
          id: normalized.id,
          sender_user_id: normalized.sender_user_id ?? null,
          is_read: isOwnMessage ? (normalized.is_read ?? false) : undefined,
        },
        {
          incrementUnread,
          avatarUrl:
            room?.type === 'direct' && !isOwnMessage
              ? typeof normalized.sender_avatar_url === 'string'
                ? normalized.sender_avatar_url
                : null
              : undefined,
        }
      );
      if (!hadRoomInList) {
        void globalApi
          .getChatRooms(true)
          .then((list) => useChatStore.getState().mergeRooms(Array.isArray(list) ? list : []))
          .catch(() => {});
      }
      dispatchAdminIncomingToast(rid, normalized, room, incrementUnread, isOwnMessage);
    }
  });

  globalWS.on('message.edit', (data) => {
    const normalizedPatch = normalizeChatMessagePatch(data, 'ws.message.edit');
    if (!normalizedPatch) return;
    const { id, patch } = normalizedPatch;
    const rooms = useChatStore.getState().messages;
    for (const [roomId, raw] of Object.entries(rooms)) {
      const msgs = asChatMessageList(raw);
      if (msgs.some((message) => chatMessageIdEqual(message.id, id))) {
        useChatStore.getState().updateMessage(Number(roomId), id, patch);
        break;
      }
    }
  });

  globalWS.on('message.delete', (data) => {
    const normalizedPatch = normalizeChatMessagePatch(data, 'ws.message.delete');
    if (!normalizedPatch) return;
    const { id, patch } = normalizedPatch;
    const deletedAt =
      typeof patch.deleted_at === 'string' && patch.deleted_at.trim()
        ? patch.deleted_at
        : new Date().toISOString();
    const rooms = useChatStore.getState().messages;
    for (const [roomId, raw] of Object.entries(rooms)) {
      const msgs = asChatMessageList(raw);
      if (msgs.some((message) => chatMessageIdEqual(message.id, id))) {
        useChatStore.getState().updateMessage(Number(roomId), id, {
          deleted_at: deletedAt,
          content: '',
          attachments: [],
          reactions: [],
        });
        break;
      }
    }
  });

  globalWS.on('typing.start', (data: Record<string, unknown>) => {
    const currentUserId = globalGetUser?.()?.id;
    if (data.user_id === currentUserId) return;
    const roomId = Number(data.room_id || useChatStore.getState().activeRoomId || 0);
    if (!roomId) return;
    const timeoutKey = `${roomId}:${data.user_id}`;
    const existingTimeout = typingTimeouts.get(timeoutKey);
    if (existingTimeout) window.clearTimeout(existingTimeout);
    useChatStore.getState().setTyping(roomId, String(data.user_id), String(data.user_name), true);
    const timeoutId = window.setTimeout(() => {
      useChatStore.getState().setTyping(roomId, String(data.user_id), String(data.user_name), false);
      typingTimeouts.delete(timeoutKey);
    }, 5000);
    typingTimeouts.set(timeoutKey, timeoutId);
  });

  globalWS.on('typing.stop', (data: Record<string, unknown>) => {
    const currentUserId = globalGetUser?.()?.id;
    if (data.user_id === currentUserId) return;
    const roomId = Number(data.room_id || useChatStore.getState().activeRoomId || 0);
    if (!roomId) return;
    const timeoutKey = `${roomId}:${data.user_id}`;
    const existingTimeout = typingTimeouts.get(timeoutKey);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
      typingTimeouts.delete(timeoutKey);
    }
    useChatStore.getState().setTyping(roomId, String(data.user_id), String(data.user_name), false);
  });

  globalWS.on('messages.read', (data: Record<string, unknown>) => {
    const { room_id, user_id, last_message_id } = data;
    const currentUserId = globalGetUser?.()?.id;
    if (!room_id || !last_message_id || String(user_id) === String(currentUserId)) return;
    const rmid = Number(room_id);
    const msgs = asChatMessageList(useChatStore.getState().messages[rmid]);
    for (const message of msgs) {
      if (
        String(message.sender_user_id) === String(currentUserId) &&
        Number(message.id) <= Number(last_message_id) &&
        !message._pending
      ) {
        useChatStore.getState().updateMessage(rmid, message.id, { is_read: true });
      }
    }
  });
}

function ensureWSClient(options: ChatWebSocketOptions, token: string) {
  if (globalWS) {
    globalPlatform = options.platform ?? 'pwa';
    globalApi = options.api;
    globalGetUser = () => options.getUser();
    return globalWS;
  }
  globalPlatform = options.platform ?? 'pwa';
  globalApi = options.api;
  globalGetUser = () => options.getUser();

  const wsUrl =
    typeof window !== 'undefined'
      ? process.env.NEXT_PUBLIC_API_URL || window.location.origin
      : process.env.NEXT_PUBLIC_API_URL || 'http://127.0.0.1:8000';

  globalWS = new WSClient({
    url: wsUrl,
    token,
    deps: options.wsDeps,
    onConnect: () => notifyConnectionSubscribers(true),
    onDisconnect: () => notifyConnectionSubscribers(false),
  });
  return globalWS;
}

export function useChatWebSocket(options: ChatWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(() => globalWS?.isConnected ?? false);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const authUserId = options.getUser()?.id ?? null;

  useEffect(() => {
    if (!optionsRef.current.isAuthenticated() || authUserId == null) return;
    globalPlatform = optionsRef.current.platform ?? 'pwa';

    let cancelled = false;
    connectionSubscribers.add(setIsConnected);

    void (async () => {
      const ensureSession = optionsRef.current.api.ensureSession;
      if (typeof ensureSession !== 'function') {
        console.warn(
          '[chat-ws] ensureSession missing on ChatApi; using cookie session fallback',
          { platform: optionsRef.current.platform }
        );
      }
      const token = (await ensureSession?.(false)) ?? '__cookie_session__';
      if (cancelled) return;
      const wsToken = token === '__cookie_session__' ? '' : token;
      const ws = ensureWSClient(optionsRef.current, wsToken);
      registerGlobalHandlers();
      setIsConnected(ws.isConnected);
      await ws.connect(wsToken || undefined);
    })();

    return () => {
      cancelled = true;
      connectionSubscribers.delete(setIsConnected);
    };
  }, [options.api, authUserId]);

  useEffect(() => {
    if (!options.isAuthenticated() || !isConnected) return;
    const heartbeatInterval = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      globalWS?.send('heartbeat');
    }, 25000);
    return () => window.clearInterval(heartbeatInterval);
  }, [options, isConnected]);

  useEffect(() => {
    if (!options.isAuthenticated()) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && globalWS && !globalWS.isConnected) {
        globalWS.reconnect();
      }
    };
    const onOnline = () => {
      if (globalWS && !globalWS.isConnected) globalWS.reconnect();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [options]);

  const sendEvent = useCallback((event: string, data: Record<string, unknown> = {}, roomId?: number) => {
    return globalWS?.send(event, data, roomId) ?? false;
  }, []);

  const sendTyping = useCallback((roomId: number, isTyping: boolean) => {
    globalWS?.send(isTyping ? 'typing.start' : 'typing.stop', {}, roomId);
  }, []);

  const markRead = useCallback((roomId: number, lastMessageId: number, topicId?: number) => {
    const payload: { last_message_id: number; topic_id?: number } = { last_message_id: lastMessageId };
    if (topicId != null) payload.topic_id = topicId;
    globalWS?.send('messages.read', payload, roomId);
  }, []);

  return { sendEvent, sendTyping, markRead, isConnected };
}

/** Subscribe to global WS connection state without starting a second client. */
export function useChatWsConnected(): boolean {
  const [isConnected, setIsConnected] = useState(() => globalWS?.isConnected ?? false);
  useEffect(() => {
    connectionSubscribers.add(setIsConnected);
    setIsConnected(globalWS?.isConnected ?? false);
    return () => {
      connectionSubscribers.delete(setIsConnected);
    };
  }, []);
  return isConnected;
}

export function chatWsSendEvent(
  event: string,
  data: Record<string, unknown> = {},
  roomId?: number
): boolean {
  return globalWS?.send(event, data, roomId) ?? false;
}

export function chatWsSendTyping(roomId: number, isTyping: boolean): void {
  globalWS?.send(isTyping ? 'typing.start' : 'typing.stop', {}, roomId);
}

export function chatWsMarkRead(roomId: number, lastMessageId: number, topicId?: number): void {
  const payload: { last_message_id: number; topic_id?: number } = { last_message_id: lastMessageId };
  if (topicId != null) payload.topic_id = topicId;
  globalWS?.send('messages.read', payload, roomId);
}
